import { normalizeRole, isAdminRole, isCLevel } from '../constants/Roles.js';
import { ROLE_PERMISSIONS, getDataScopeForRole, canAccessScope as canAccessScopeFn } from '../constants/Permissions.js';
import { DEPRECATED_UNUSED } from '../constants/PermissionMatrix.js';
import { auth } from './AuthService.js';
import { RolePermissionService } from './RolePermissionService.js';

const ALL_ACTIONS = ['view', 'create', 'edit', 'delete', 'view_amount', 'export', 'assign', 'manage'];

const USERS_EXTRA_ACTIONS = ['assign_admin', 'assign_super_admin'];

const DATA_MANAGEMENT_ACTIONS = ['deals', 'accounts', 'contacts', 'users', 'goals', 'commissions', 'activities', 'purge_all', 'import_deals', 'import_accounts', 'import_contacts'];

const DATA_MANAGEMENT_ENTITY_TYPES = ['deals', 'accounts', 'contacts', 'users', 'goals', 'commissions', 'activities'];

const IMPORT_ACTION_MAP = { deals: 'import_deals', accounts: 'import_accounts', contacts: 'import_contacts' };

export class PermissionService {
    static _loaded = false;
    static _loadPromise = null;
    static _effectivePermissions = {};

    static getRole() {
        return auth.getSession()?.role || null;
    }

    static getUserId() {
        return auth.getSession()?.userId || null;
    }

    static isLoaded() {
        return this._loaded;
    }

    /** Drop cached permissions so the next user cannot inherit the previous session. */
    static reset() {
        this._loaded = false;
        this._loadPromise = null;
        this._effectivePermissions = {};
    }

    static async load() {
        if (this._loaded) return;
        if (this._loadPromise) return this._loadPromise;

        this._loadPromise = (async () => {
            try {
                const role = this.getRole();
                const userId = this.getUserId();
                if (!role) {
                    this._effectivePermissions = {};
                    this._loaded = true;
                    return;
                }

                let perms = {};

                try {
                    const data = await RolePermissionService.getUserEffectivePermissions(userId);
                    if (data?.effective_permissions) {
                        perms = data.effective_permissions;
                    }
                } catch (err) {
                    console.warn('PermissionService: getUserEffectivePermissions failed, trying role permissions', err);
                    try {
                        const roleData = await RolePermissionService.getRolePermissions(role);
                        if (roleData?.permissions) {
                            for (const p of roleData.permissions) {
                                if (!perms[p.module]) perms[p.module] = {};
                                perms[p.module][p.action] = p.allowed;
                            }
                        }
                    } catch (err2) {
                        console.warn('PermissionService: getRolePermissions failed, using fallback', err2);
                        perms = this._getFallbackPermissions(role);
                    }
                }

                // Admin (Executive) follows the permission matrix like every other
                // role. Only Super Admin bypasses via can() — do not re-grant
                // hardcoded ROLE_PERMISSIONS defaults here.
                this._effectivePermissions = perms;
                await auth.refreshScopes().catch(() => null);
                this._loaded = true;
            } catch {
                this._effectivePermissions = {};
                this._loaded = true;
            }
        })();

        await this._loadPromise;
    }

    static _getFallbackPermissions(role) {
        const rolePerms = ROLE_PERMISSIONS[normalizeRole(role)] || {};
        const result = {};
        const SCOPE_ACTIONS = ['company', 'team', 'own', 'assigned', 'customize'];
        for (const [module, actions] of Object.entries(rolePerms)) {
            result[module] = {};
            if (module === 'data_management') {
                for (const action of DATA_MANAGEMENT_ACTIONS) {
                    result[module][action] = actions.includes(action);
                }
                continue;
            }
            for (const action of ALL_ACTIONS) {
                const hasAction = actions.includes(action)
                    || actions.includes(`${action}:all`)
                    || actions.includes(`${action}:team`)
                    || actions.includes(`${action}:own`)
                    || actions.includes(`${action}:assigned`)
                    || actions.some(a => a.startsWith(`${action}:`));
                result[module][action] = hasAction;
            }
            if (module === 'dashboard') {
                result[module]['view'] = actions.some(a => SCOPE_ACTIONS.includes(a));
                result[module]['edit'] = actions.includes('customize');
            }
        }
        return result;
    }

    static async reload() {
        this._loaded = false;
        this._loadPromise = null;
        await this.load();
    }

    static _direct(module, action) {
        if (DEPRECATED_UNUSED.includes(action)) return false;
        return this._effectivePermissions[module]?.[action] === true;
    }

    static can(module, action) {
        const role = normalizeRole(this.getRole());
        // Hardcoded bypass — Super Admin never relies on role_permissions rows.
        if (role === 'super_admin') return true;
        if (!this._loaded) return false;
        if (DEPRECATED_UNUSED.includes(action)) return false;

        return this._direct(module, action);
    }

    static canView(module) { return this.can(module, 'view'); }
    static canCreate(module) { return this.can(module, 'create'); }
    static canEdit(module) { return this.can(module, 'edit'); }
    static canDelete(module) { return this.can(module, 'delete'); }
    static canViewFinancials(module) { return this.can(module, 'view_amount'); }
    static canExport(module) { return this.can(module, 'export'); }
    static canAssign(module) { return this.can(module, 'assign'); }
    static canManageOrganization(module) { return this.can(module, 'manage_organization'); }

    static financialText(module, formatted) {
        return this.canViewFinancials(module) ? formatted : '—';
    }

    static canDataManagement(entityType) {
        const key = entityType === 'clients' ? 'accounts' : entityType;
        return this.can('data_management', key);
    }

    static canPurgeAllData() {
        return this.can('data_management', 'purge_all');
    }

    static canImport(entityType) {
        const key = entityType === 'clients' ? 'accounts' : entityType;
        const action = IMPORT_ACTION_MAP[key];
        if (!action) return false;
        if (this.can('data_management', action)) return true;
        return this.canDataManagement(key);
    }

    static canAccessDataManagement() {
        return DATA_MANAGEMENT_ENTITY_TYPES.some(e => this.canDataManagement(e))
            || Object.values(IMPORT_ACTION_MAP).some(a => this.can('data_management', a))
            || this.canPurgeAllData();
    }

    static isAdmin() {
        const role = normalizeRole(this.getRole());
        return isAdminRole(role);
    }

    static isCLevel() {
        const role = normalizeRole(this.getRole());
        return isCLevel(role);
    }

    static getDataScope() {
        return getDataScopeForRole(this.getRole());
    }

    static getModuleScope(module = 'deals') {
        if (normalizeRole(this.getRole()) === 'super_admin') return 'all';
        const scopes = auth.getSession()?.scopes;
        const value = scopes && typeof scopes === 'object' ? scopes[module] : null;
        if (value === 'own' || value === 'team' || value === 'all') return value;
        return 'own';
    }

    static canSeeBeyondOwn(module = 'deals') {
        const scope = this.getModuleScope(module);
        return scope === 'team' || scope === 'all';
    }

    static getPermissions() {
        const all = [];
        for (const [module, actions] of Object.entries(this._effectivePermissions)) {
            for (const [action, allowed] of Object.entries(actions)) {
                if (allowed && !DEPRECATED_UNUSED.includes(action)) {
                    all.push(`${module}:${action}`);
                }
            }
        }
        return all;
    }

    static getModulePermissions(module) {
        const mod = this._effectivePermissions[module] || {};
        return Object.entries(mod)
            .filter(([k, v]) => v && !DEPRECATED_UNUSED.includes(k))
            .map(([k]) => k);
    }

    static canAccessScope(requiredScope) {
        return canAccessScopeFn(this.getRole(), requiredScope);
    }

    static canViewOrgDeals() {
        return this.can('deals', 'view');
    }

    static canManageClients() {
        return this.canCreate('accounts') || this.canEdit('accounts');
    }

    static canManageContacts() {
        return this.canCreate('contacts') || this.canEdit('contacts');
    }

    static canManageDeals() {
        return this.canCreate('deals') || this.canEdit('deals');
    }

    static canViewSettings() {
        return this.can('settings', 'view');
    }

    static canManageUsers() {
        return this.canCreate('users') || this.canEdit('users');
    }
}

export const permissions = PermissionService;
